import React, { useState, useEffect, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as faSolidHeart, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faRegularHeart } from "@fortawesome/free-regular-svg-icons";
import API_ENDPOINTS, { IMAGE_BASE_URL } from "../../api/apiConfig";
import { useUser } from "../../context/UserContext";
import type { Pagination, Product } from "../../types";
import "../../assets/css/theme.css";
import "../../assets/css/landing.css"; // product-card styles (.ffh-pc*)
import "../../assets/css/shop.css";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
];

/** "banarasi-silk" → "Banarasi silk" with the last word set in gold italic */
const renderTitle = (raw?: string) => {
  const text = raw ? raw.replace(/-/g, " ") : "Our Products";
  const words = text.split(" ");
  if (words.length === 1) return <em>{text}</em>;
  const last = words.pop();
  return (
    <>
      {words.join(" ")} <em>{last}</em>
    </>
  );
};

const Products = () => {
  const { subcategory } = useParams();
  const [sortBy, setSortBy] = useState("newest");
  const [products, setProducts] = useState<Product[]>([]);
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    per_page: 12,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  });
  const token = localStorage.getItem("authToken");

  const fetchProducts = async (page = 1, perPage = 12) => {
    setLoading(true);
    setError("");
    const body: { page: number; per_page: number; sc_slug?: string } = { page, per_page: perPage };
    if (subcategory) body.sc_slug = subcategory;

    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(API_ENDPOINTS.PRODUCTSBYPAGE, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (response.ok && data.status && Array.isArray(data.data)) {
        setProducts(data.data);
        setPagination(
          data.pagination || {
            current_page: page,
            per_page: perPage,
            total_count: data.data.length,
            total_pages: 1,
            has_next: false,
            has_previous: false,
          }
        );
      } else {
        setProducts([]);
        setError(data.message || "Failed to fetch products.");
      }
    } catch (err) {
      setProducts([]);
      setError(`An error occurred: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [subcategory]);

  const handleWishlistToggle = async (productId: number) => {
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(API_ENDPOINTS.WISHLISTTOGGLE, {
        method: "POST",
        headers,
        body: JSON.stringify({ product_id: productId }),
      });
      const data = await response.json();
      if (response.ok) fetchProducts(pagination.current_page, pagination.per_page);
      else alert(data.message || "Failed to update wishlist.");
    } catch (err) {
      console.error("Wishlist toggle error:", err);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchProducts(newPage, pagination.per_page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    if (sortBy === "price-low") return Number(a.p_price) - Number(b.p_price);
    if (sortBy === "price-high") return Number(b.p_price) - Number(a.p_price);
    return new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime();
  });

  const renderPageNumbers = () => {
    const { current_page, total_pages } = pagination;
    const items: ReactElement[] = [];
    for (let i = 1; i <= total_pages; i++) {
      const near =
        i === 1 ||
        i === total_pages ||
        (i >= current_page - 1 && i <= current_page + 1);
      const ellipsis = i === current_page - 2 || i === current_page + 2;
      if (near) {
        items.push(
          <button
            key={i}
            className={`ffs-pag-num${current_page === i ? " is-active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      } else if (ellipsis) {
        items.push(
          <span key={`e-${i}`} className="ffs-pag-dots">
            ···
          </span>
        );
      }
    }
    return items;
  };

  const isVendor = !!token && !!user?.created_by;

  const cardImage = (product: Product) => {
    const first = product.images?.[0];
    return first?.startsWith("http") ? first : `${IMAGE_BASE_URL}${first}`;
  };

  const rangeStart = (pagination.current_page - 1) * pagination.per_page + 1;
  const rangeEnd = Math.min(pagination.current_page * pagination.per_page, pagination.total_count);

  return (
    <main className="ffs-page">
      {/* ── PAGE HEADER (compact – products stay above the fold) ── */}
      <header className="ffs-hero">
        <div className="ffh-container ffs-hero-inner">
          <div>
            <nav className="ffs-crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              {subcategory ? <Link to="/products">Products</Link> : <span className="is-current">Products</span>}
              {subcategory && (
                <>
                  <span aria-hidden="true">/</span>
                  <span className="is-current">{subcategory.replace(/-/g, " ")}</span>
                </>
              )}
            </nav>
            <h1 className="ffs-hero-title">{renderTitle(subcategory)}</h1>
          </div>
          <p className="ffs-hero-sub">Handpicked styles, crafted with intention.</p>
        </div>
      </header>

      {/* ── TOOLBAR ── */}
      <div className="ffs-toolbar">
        <div className="ffh-container ffs-toolbar-inner">
          <p className="ffs-count">
            {pagination.total_count > 0 ? (
              <>
                <strong>{rangeStart}–{rangeEnd}</strong> of {pagination.total_count} pieces
              </>
            ) : (
              "No products"
            )}
          </p>

          <div className="ffs-sort" role="radiogroup" aria-label="Sort products">
            <span className="ffs-sort-label">Sort</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={sortBy === opt.value}
                className={`ffs-sort-btn${sortBy === opt.value ? " is-active" : ""}`}
                onClick={() => setSortBy(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── PRODUCT GRID ── */}
      <section className="ffs-catalog">
        <div className="ffh-container">
          {loading ? (
            <div className="ffs-grid" aria-busy="true" aria-label="Loading products">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="ffs-skel" key={i}>
                  <div className="ffs-skel-media" />
                  <div className="ffs-skel-line" />
                  <div className="ffs-skel-line ffs-skel-line--short" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="ffs-state ffs-state--error">
              <span className="ffs-state-icon" aria-hidden="true">!</span>
              <p>{error}</p>
              <button type="button" className="ffh-btn" onClick={() => fetchProducts()}>Try again</button>
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="ffs-state">
              <span className="ffs-state-icon" aria-hidden="true">∅</span>
              <p>No products found.</p>
              {subcategory && <Link to="/products" className="ffh-btn">View all products</Link>}
            </div>
          ) : (
            <div className="ffs-grid">
              {sortedProducts.map((product, idx) => {
                const originalPrice = parseFloat(String(product.p_price)) || 0;
                const discountPct = parseFloat(String(product.discount_percentage)) || 0;
                const discounted = Math.round(originalPrice * (1 - discountPct / 100));
                const productUrl = `/product/${product.p_slug}`;

                return (
                  <article
                    key={product.p_id}
                    className="ffh-pc"
                    style={{ animationDelay: `${Math.min(idx, 11) * 0.05}s` }}
                  >
                    <div className="ffh-pc-media">
                      <a href={productUrl} className="ffh-pc-link" aria-label={product.p_name}>
                        <img src={cardImage(product)} alt={product.p_name} loading="lazy" />
                      </a>

                      {discountPct > 0 && (
                        <span className="ffh-pc-badge">−{product.discount_percentage}%</span>
                      )}

                      {token && (
                        <button
                          type="button"
                          className={`ffh-pc-wish${product.is_wishlisted ? " is-active" : ""}`}
                          onClick={() => handleWishlistToggle(product.p_id)}
                          aria-label={product.is_wishlisted ? "Remove from wishlist" : "Add to wishlist"}
                        >
                          <FontAwesomeIcon icon={product.is_wishlisted ? faSolidHeart : faRegularHeart} />
                        </button>
                      )}

                      <a href={productUrl} className="ffh-pc-quick">View product</a>
                    </div>

                    <div className="ffh-pc-body">
                      <a href={productUrl} className="ffh-pc-name">{product.p_name}</a>
                      <div className="ffh-pc-price">
                        {isVendor ? (
                          <>
                            <span className="ffh-pc-now">₹{discounted.toLocaleString("en-IN")}</span>
                            {discountPct > 0 && (
                              <span className="ffh-pc-orig">₹{originalPrice.toLocaleString("en-IN")}</span>
                            )}
                          </>
                        ) : (
                          <span className="ffh-pc-now">₹{originalPrice.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* ── PAGINATION ── */}
          {!loading && pagination.total_pages > 1 && (
            <nav className="ffs-pag" aria-label="Product pagination">
              <button
                type="button"
                className="ffs-pag-nav"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_previous}
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className="ffs-pag-nums">{renderPageNumbers()}</div>

              <button
                type="button"
                className="ffs-pag-nav"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next}
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </nav>
          )}
        </div>
      </section>
    </main>
  );
};

export default Products;